import { FC } from 'react'
import Error from 'next/error'
import { GetServerSideProps } from 'next'
import { StoryblokResult } from 'storyblok-js-client'

import { storyblokInstance } from '@app/services/storyblok-service'
import { Bridge } from '@app/components/bridge'
import { countryCodeToLocale } from '@app/utils/i18n'
import { useEditor } from '@app/hooks/use-editor'
import { LayoutPage } from '@app/components/layout-page'
import { Page } from '@app/components/page'
import { linkService, LinkMap } from '@app/services/link-service'

type Props = {
  page: StoryblokResult | null
  layout: StoryblokResult | null
  locale: string | null
  linkMap: LinkMap | null
  error?: { statusCode: number }
}

const PageWrapper: FC<Props> = ({ error, page, layout }) => {
  if (error) {
    return <Error statusCode={error.statusCode} />
  }

  if (!page) {
    return null
  }

  const pageContent = useEditor(page.data.story.content)
  const layoutContent = layout ? layout.data.story.content : {}

  return (
    <Bridge>
      <LayoutPage {...layoutContent}>
        <Page {...pageContent} />
      </LayoutPage>
    </Bridge>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
  res,
}) => {
  storyblokInstance.setQuery(query)

  const { country = 'gb', slug = '' } = query
  const path = Array.isArray(slug) ? slug.join('/') : slug
  const countryCode = Array.isArray(country) ? country[0] : country
  const locale = countryCodeToLocale(countryCode)

  let storyblokPath = path.replace('help', '')
  if (storyblokPath === '') {
    storyblokPath = '/home'
  }

  let page = null
  let layout = null

  try {
    const url =
      countryCode === 'gb'
        ? `cdn/stories/${storyblokPath}`
        : `cdn/stories/${locale}/${storyblokPath}`
    page = await storyblokInstance.get(url, {
      resolve_relations: 'global-reference.reference',
    })

    const { layout: uuid } = page.data.story.content
    if (uuid) {
      layout = await storyblokInstance.getLayout(locale, uuid)
    }

    return {
      props: {
        page,
        layout,
        linkMap: linkService.linkMap,
        locale,
      },
    }
  } catch (e) {
    res.statusCode = 404
    return {
      props: {
        page,
        layout,
        linkMap: linkService.linkMap,
        locale,
        error: { statusCode: e.response.status },
      },
    }
  }
}

export default PageWrapper
